<template>
  <div class="skill-file-tree">
    <div v-if="!treeNodes.length && !stagedFiles.length" class="empty-message">
      No files uploaded.
    </div>

    <div
      v-for="node in sortedTreeNodes"
      :key="node.path"
      class="tree-node"
      :class="{ 'is-dir': node.isDirectory }"
      :style="{ paddingLeft: nodeDepth(node) * 1.25 + 'rem' }"
    >
      <div class="node-row">
        <span v-if="node.isDirectory" class="node-icon"
          ><svg viewBox="0 0 16 16" class="icon-svg" aria-hidden="true">
            <path
              fill="currentColor"
              d="M9.828 3h3.982a2 2 0 011.992 2.181l-.637 7A2 2 0 0113.174 14H2.828a2 2 0 01-1.991-1.819l-.637-7a1.99 1.99 0 01.342-1.31L.5 3a2 2 0 012-2h3.672a2 2 0 011.414.586l.828.828A2 2 0 009.828 3zm-8.322.12C1.72 3.042 1.95 3 2.19 3h5.396l-.707-.707A1 1 0 006.172 2H2.5a1 1 0 00-1 .981l.006.139z"
            /></svg
        ></span>
        <span v-else class="node-icon"
          ><svg viewBox="0 0 16 16" class="icon-svg" aria-hidden="true">
            <path
              fill="currentColor"
              d="M4 0h5.293A1 1 0 0110 .293L13.707 4a1 1 0 01.293.707V14a2 2 0 01-2 2H4a2 2 0 01-2-2V2a2 2 0 012-2zm5.5 1.5v2a1 1 0 001 1h2l-3-3z"
            /></svg
        ></span>
        <span class="node-name">{{ nodeName(node) }}</span>
        <span v-if="!node.isDirectory && node.size != null" class="node-size">{{ formatSize(node.size) }}</span>
        <button v-if="!node.isDirectory && skillName" type="button" class="action-btn icon-btn" title="Download file" @click="downloadNode(node)">
          <svg viewBox="0 0 16 16" class="icon-svg" aria-hidden="true">
            <path
              fill="currentColor"
              d="M.5 9.9a.5.5 0 01.5.5v2.5a1 1 0 001 1h12a1 1 0 001-1v-2.5a.5.5 0 011 0v2.5a2 2 0 01-2 2H2a2 2 0 01-2-2v-2.5a.5.5 0 01.5-.5zm7.646-9.754a.5.5 0 01.708 0l3 3a.5.5 0 01-.708.708L8.5 2.707V11.5a.5.5 0 01-1 0V2.707L5.354 3.854a.5.5 0 11-.708-.708l3-3z"
              transform="scale(1,-1) translate(0,-16)"
            />
          </svg>
        </button>
        <button
          v-if="!disabled && !node.isDirectory"
          type="button"
          class="action-btn icon-btn"
          title="Upload file"
          @click="triggerReplace(node.path)"
        >
          <svg viewBox="0 0 16 16" class="icon-svg" aria-hidden="true">
            <path
              fill="currentColor"
              d="M.5 9.9a.5.5 0 01.5.5v2.5a1 1 0 001 1h12a1 1 0 001-1v-2.5a.5.5 0 011 0v2.5a2 2 0 01-2 2H2a2 2 0 01-2-2v-2.5a.5.5 0 01.5-.5zm7.646-9.754a.5.5 0 01.708 0l3 3a.5.5 0 01-.708.708L8.5 2.707V11.5a.5.5 0 01-1 0V2.707L5.354 3.854a.5.5 0 11-.708-.708l3-3z"
            />
          </svg>
        </button>
        <template v-if="!disabled && node.isDirectory">
          <button type="button" class="action-btn icon-btn" title="New subfolder" @click="openNewSubfolder(node.path)">
            <svg viewBox="0 0 16 16" class="icon-svg" aria-hidden="true">
              <path
                fill="currentColor"
                d="M9.828 3h3.982a2 2 0 011.992 2.181l-.637 7A2 2 0 0113.174 14H2.828a2 2 0 01-1.991-1.819l-.637-7a1.99 1.99 0 01.342-1.31L.5 3a2 2 0 012-2h3.672a2 2 0 011.414.586l.828.828A2 2 0 009.828 3zm-8.322.12C1.72 3.042 1.95 3 2.19 3h5.396l-.707-.707A1 1 0 006.172 2H2.5a1 1 0 00-1 .981l.006.139z"
              />
              <path
                fill="currentColor"
                fill-rule="evenodd"
                d="M11.5 9.5v-2a.5.5 0 00-1 0v2H8a.5.5 0 000 1h2.5v2a.5.5 0 001 0v-2H14a.5.5 0 000-1h-2.5z"
              />
            </svg>
          </button>
          <button type="button" class="action-btn icon-btn" title="Upload here" @click="triggerFolderUpload(node.path)">
            <svg viewBox="0 0 16 16" class="icon-svg" aria-hidden="true">
              <path
                fill="currentColor"
                d="M.5 9.9a.5.5 0 01.5.5v2.5a1 1 0 001 1h12a1 1 0 001-1v-2.5a.5.5 0 011 0v2.5a2 2 0 01-2 2H2a2 2 0 01-2-2v-2.5a.5.5 0 01.5-.5zm7.646-9.754a.5.5 0 01.708 0l3 3a.5.5 0 01-.708.708L8.5 2.707V11.5a.5.5 0 01-1 0V2.707L5.354 3.854a.5.5 0 11-.708-.708l3-3z"
              />
            </svg>
          </button>
        </template>
        <button
          v-if="!disabled"
          type="button"
          class="action-btn icon-btn delete-btn"
          :title="node.isDirectory ? 'Delete folder' : 'Delete'"
          @click="deleteNode(node)"
        >
          <svg viewBox="0 0 16 16" class="icon-svg" aria-hidden="true">
            <path
              fill="currentColor"
              d="M5.5 5.5A.5.5 0 016 6v6a.5.5 0 01-1 0V6a.5.5 0 01.5-.5zm2.5 0a.5.5 0 01.5.5v6a.5.5 0 01-1 0V6a.5.5 0 01.5-.5zm3 .5a.5.5 0 00-1 0v6a.5.5 0 001 0V6z"
            />
            <path
              fill="currentColor"
              fill-rule="evenodd"
              d="M14.5 3a1 1 0 01-1 1H13v9a2 2 0 01-2 2H5a2 2 0 01-2-2V4h-.5a1 1 0 01-1-1V2a1 1 0 011-1H6a1 1 0 011-1h2a1 1 0 011 1h3.5a1 1 0 011 1v1zM4.118 4L4 4.059V13a1 1 0 001 1h6a1 1 0 001-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"
            />
          </svg>
        </button>
      </div>
      <div v-if="newSubfolderFor === node.path" class="inline-input-row">
        <input
          :ref="'subfolder-' + node.path"
          v-model="newSubfolderName"
          class="folder-name-input"
          placeholder="Subfolder name"
          @keydown.enter.prevent="addSubfolder"
        />
        <button type="button" class="action-btn" @click="addSubfolder">Create</button>
        <button
          type="button"
          class="action-btn"
          @click="
            newSubfolderFor = null;
            newSubfolderName = '';
          "
        >
          Cancel
        </button>
      </div>
      <!-- staged files inside this folder -->
      <div
        v-for="(item, idx) in stagedInFolder(node.path)"
        :key="'staged-in-' + node.path + '-' + idx"
        class="tree-node staged"
        :style="{ paddingLeft: (nodeDepth(node) + 1) * 1.25 + 'rem' }"
      >
        <div class="node-row">
          <span class="node-icon"
            ><svg viewBox="0 0 16 16" class="icon-svg" aria-hidden="true">
              <path
                fill="currentColor"
                d="M4 0h5.293A1 1 0 0110 .293L13.707 4a1 1 0 01.293.707V14a2 2 0 01-2 2H4a2 2 0 01-2-2V2a2 2 0 012-2zm5.5 1.5v2a1 1 0 001 1h2l-3-3z"
              /></svg
          ></span>
          <span class="node-name">{{ item.file.name }}</span>
          <span class="node-staged-badge">Staged</span>
          <button v-if="!disabled" type="button" class="action-btn icon-btn delete-btn" title="Remove" @click="removeStagedFileByRef(item)">
            <svg viewBox="0 0 16 16" class="icon-svg" aria-hidden="true">
              <path
                fill="currentColor"
                d="M5.5 5.5A.5.5 0 016 6v6a.5.5 0 01-1 0V6a.5.5 0 01.5-.5zm2.5 0a.5.5 0 01.5.5v6a.5.5 0 01-1 0V6a.5.5 0 01.5-.5zm3 .5a.5.5 0 00-1 0v6a.5.5 0 001 0V6z"
              />
              <path
                fill="currentColor"
                fill-rule="evenodd"
                d="M14.5 3a1 1 0 01-1 1H13v9a2 2 0 01-2 2H5a2 2 0 01-2-2V4h-.5a1 1 0 01-1-1V2a1 1 0 011-1H6a1 1 0 011-1h2a1 1 0 011 1h3.5a1 1 0 011 1v1zM4.118 4L4 4.059V13a1 1 0 001 1h6a1 1 0 001-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>

    <!-- root-level staged files -->
    <div v-for="(item, idx) in stagedInFolder('')" :key="'staged-root-' + idx" class="tree-node staged">
      <div class="node-row">
        <span class="node-icon"
          ><svg viewBox="0 0 16 16" class="icon-svg" aria-hidden="true">
            <path
              fill="currentColor"
              d="M4 0h5.293A1 1 0 0110 .293L13.707 4a1 1 0 01.293.707V14a2 2 0 01-2 2H4a2 2 0 01-2-2V2a2 2 0 012-2zm5.5 1.5v2a1 1 0 001 1h2l-3-3z"
            /></svg
        ></span>
        <span class="node-name">{{ item.file.name }}</span>
        <span class="node-staged-badge">Staged</span>
        <button v-if="!disabled" type="button" class="action-btn icon-btn delete-btn" title="Remove" @click="removeStagedFileByRef(item)">
          <svg viewBox="0 0 16 16" class="icon-svg" aria-hidden="true">
            <path
              fill="currentColor"
              d="M5.5 5.5A.5.5 0 016 6v6a.5.5 0 01-1 0V6a.5.5 0 01.5-.5zm2.5 0a.5.5 0 01.5.5v6a.5.5 0 01-1 0V6a.5.5 0 01.5-.5zm3 .5a.5.5 0 00-1 0v6a.5.5 0 001 0V6z"
            />
            <path
              fill="currentColor"
              fill-rule="evenodd"
              d="M14.5 3a1 1 0 01-1 1H13v9a2 2 0 01-2 2H5a2 2 0 01-2-2V4h-.5a1 1 0 01-1-1V2a1 1 0 011-1H6a1 1 0 011-1h2a1 1 0 011 1h3.5a1 1 0 011 1v1zM4.118 4L4 4.059V13a1 1 0 001 1h6a1 1 0 001-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"
            />
          </svg>
        </button>
      </div>
    </div>

    <div v-if="!disabled" class="file-tree-actions">
      <button type="button" class="action-btn icon-text-btn" @click="openNewFolder">
        <svg viewBox="0 0 16 16" class="icon-svg" aria-hidden="true">
          <path
            fill="currentColor"
            d="M9.828 3h3.982a2 2 0 011.992 2.181l-.637 7A2 2 0 0113.174 14H2.828a2 2 0 01-1.991-1.819l-.637-7a1.99 1.99 0 01.342-1.31L.5 3a2 2 0 012-2h3.672a2 2 0 011.414.586l.828.828A2 2 0 009.828 3zm-8.322.12C1.72 3.042 1.95 3 2.19 3h5.396l-.707-.707A1 1 0 006.172 2H2.5a1 1 0 00-1 .981l.006.139z"
          />
          <path fill="currentColor" fill-rule="evenodd" d="M11.5 9.5v-2a.5.5 0 00-1 0v2H8a.5.5 0 000 1h2.5v2a.5.5 0 001 0v-2H14a.5.5 0 000-1h-2.5z" />
        </svg>
        New Folder
      </button>
      <button type="button" class="action-btn icon-text-btn" @click="triggerFileUpload">
        <svg viewBox="0 0 16 16" class="icon-svg" aria-hidden="true">
          <path
            fill="currentColor"
            d="M.5 9.9a.5.5 0 01.5.5v2.5a1 1 0 001 1h12a1 1 0 001-1v-2.5a.5.5 0 011 0v2.5a2 2 0 01-2 2H2a2 2 0 01-2-2v-2.5a.5.5 0 01.5-.5zm7.646-9.754a.5.5 0 01.708 0l3 3a.5.5 0 01-.708.708L8.5 2.707V11.5a.5.5 0 01-1 0V2.707L5.354 3.854a.5.5 0 11-.708-.708l3-3z"
          />
        </svg>
        Upload File
      </button>
      <input ref="fileInput" type="file" multiple style="display: none" accept=".docx,.png,.txt,.xml,.md" @change="onFilesSelected" />
      <input ref="replaceInput" type="file" style="display: none" @change="onReplaceFileSelected" />
    </div>

    <div v-if="showNewFolderInput && !disabled" class="new-folder-row">
      <input ref="newFolderInput" v-model="newFolderName" class="folder-name-input" placeholder="Folder name" @keydown.enter.prevent="addFolder" />
      <button type="button" class="action-btn" @click="addFolder">Create</button>
      <button
        type="button"
        class="action-btn"
        @click="
          showNewFolderInput = false;
          newFolderName = '';
        "
      >
        Cancel
      </button>
    </div>

    <div v-if="showUploadDialog && !disabled" class="upload-dialog">
      <div class="upload-dialog-title">Choose destination folder</div>
      <div class="destination-options">
        <label> <input v-model="uploadDestination" type="radio" value="" /> Root </label>
        <label v-for="folder in allFolders" :key="folder"> <input v-model="uploadDestination" type="radio" :value="folder" /> {{ folder }} </label>
      </div>
      <div class="upload-dialog-files">
        <div v-for="(f, idx) in pendingUploadFiles" :key="idx">{{ f.name }}</div>
      </div>
      <div class="upload-dialog-actions">
        <button type="button" class="action-btn" @click="confirmUpload">Upload</button>
        <button type="button" class="action-btn" @click="cancelUpload">Cancel</button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SkillFileTree',
  props: {
    initialFiles: {
      type: Array,
      default: () => []
    },
    skillName: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    },
    version: {
      type: Number,
      default: null
    }
  },
  data() {
    return {
      treeNodes: [...(this.initialFiles || [])],
      stagedFiles: [],
      newFolders: [],
      deletePaths: [],
      showNewFolderInput: false,
      newFolderName: '',
      newSubfolderFor: null,
      newSubfolderName: '',
      showUploadDialog: false,
      pendingUploadFiles: [],
      uploadDestination: '',
      skipUploadDialog: false,
      replaceTargetPath: ''
    };
  },
  computed: {
    allFolders() {
      const dirs = new Set(this.treeNodes.filter(n => n.isDirectory).map(n => n.path));
      for (const f of this.newFolders) dirs.add(f);
      return [...dirs];
    },
    sortedTreeNodes() {
      const dirSet = new Set(this.treeNodes.filter(n => n.isDirectory).map(n => n.path));
      const sortKey = node => {
        const segments = node.path.split('/');
        return segments
          .map((seg, i) => {
            const partialPath = segments.slice(0, i + 1).join('/');
            return (dirSet.has(partialPath) ? '0' : '1') + seg;
          })
          .join('/');
      };
      return [...this.treeNodes].sort((a, b) => {
        const ka = sortKey(a);
        const kb = sortKey(b);
        return ka < kb ? -1 : ka > kb ? 1 : 0;
      });
    }
  },
  watch: {
    initialFiles(files) {
      this.treeNodes = [...(files || [])];
    }
  },
  methods: {
    nodeName(node) {
      const parts = (node.path || '').split('/');
      return parts[parts.length - 1] || node.path;
    },
    nodeDepth(node) {
      return (node.path || '').split('/').length - 1;
    },
    stagedInFolder(folderPath) {
      return this.stagedFiles.filter(item => {
        const dest = item.destinations[0] || '';
        const parentFolder = dest.includes('/')
          ? dest
              .split('/')
              .slice(0, -1)
              .join('/')
          : '';
        return parentFolder === folderPath;
      });
    },
    removeStagedFileByRef(item) {
      const idx = this.stagedFiles.indexOf(item);
      if (idx !== -1) this.stagedFiles.splice(idx, 1);
      this.emitChange();
    },
    openNewFolder() {
      this.showNewFolderInput = !this.showNewFolderInput;
      if (this.showNewFolderInput) {
        this.$nextTick(() => this.$refs.newFolderInput && this.$refs.newFolderInput.focus());
      }
    },
    openNewSubfolder(folderPath) {
      this.newSubfolderFor = folderPath;
      this.newSubfolderName = '';
      this.$nextTick(() => {
        const ref = this.$refs['subfolder-' + folderPath];
        const el = Array.isArray(ref) ? ref[0] : ref;
        if (el) el.focus();
      });
    },
    addSubfolder() {
      const name = this.newSubfolderName.trim();
      if (!name) return;
      const fullPath = `${this.newSubfolderFor}/${name}`;
      this.newFolders.push(fullPath);
      this.treeNodes.push({ path: fullPath, isDirectory: true });
      this.newSubfolderFor = null;
      this.newSubfolderName = '';
      this.emitChange();
    },
    triggerFolderUpload(folderPath) {
      this.uploadDestination = folderPath;
      this.skipUploadDialog = true;
      this.$refs.fileInput.value = '';
      this.$refs.fileInput.click();
    },
    formatSize(bytes) {
      if (bytes == null) return '';
      if (bytes < 1024) return `${bytes} B`;
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    },
    deleteNode(node) {
      if (node.isDirectory) {
        // Delete folder and all children
        const prefix = node.path + '/';
        const childPaths = this.treeNodes.filter(n => !n.isDirectory && n.path.startsWith(prefix)).map(n => n.path);
        this.deletePaths.push(...childPaths, node.path);
        this.treeNodes = this.treeNodes.filter(n => n.path !== node.path && !n.path.startsWith(prefix));
      } else {
        this.deletePaths.push(node.path);
        this.treeNodes = this.treeNodes.filter(n => n.path !== node.path);
      }
      this.emitChange();
    },
    addFolder() {
      const name = this.newFolderName.trim();
      if (!name) return;
      this.newFolders.push(name);
      this.treeNodes.push({ path: name, name, isDirectory: true });
      this.newFolderName = '';
      this.showNewFolderInput = false;
      this.emitChange();
    },
    triggerFileUpload() {
      this.uploadDestination = '';
      this.skipUploadDialog = false;
      this.$refs.fileInput.value = '';
      this.$refs.fileInput.click();
    },
    onFilesSelected(e) {
      this.pendingUploadFiles = Array.from(e.target.files);
      if (!this.pendingUploadFiles.length) return;
      if (this.skipUploadDialog) {
        this.confirmUpload();
      } else {
        this.showUploadDialog = true;
      }
    },
    confirmUpload() {
      for (const file of this.pendingUploadFiles) {
        const dest = this.uploadDestination ? `${this.uploadDestination}/${file.name}` : file.name;
        this.stagedFiles.push({ file, destinations: [dest] });
      }
      this.pendingUploadFiles = [];
      this.showUploadDialog = false;
      this.emitChange();
    },
    cancelUpload() {
      this.pendingUploadFiles = [];
      this.showUploadDialog = false;
    },
    removeStagedFile(idx) {
      this.stagedFiles.splice(idx, 1);
      this.emitChange();
    },
    triggerReplace(path) {
      this.replaceTargetPath = path;
      this.$refs.replaceInput.value = '';
      this.$refs.replaceInput.click();
    },
    onReplaceFileSelected(e) {
      const [file] = Array.from(e.target.files || []);
      if (!file || !this.replaceTargetPath) {
        this.replaceTargetPath = '';
        return;
      }

      // Remove the original file node from the tree so only the staged entry shows
      this.treeNodes = this.treeNodes.filter(n => n.path !== this.replaceTargetPath);
      if (!this.deletePaths.includes(this.replaceTargetPath)) {
        this.deletePaths.push(this.replaceTargetPath);
      }

      const existing = this.stagedFiles.find(item => (item.destinations || []).includes(this.replaceTargetPath));
      if (existing) {
        existing.file = file;
        existing.destinations = [this.replaceTargetPath];
      } else {
        this.stagedFiles.push({ file, destinations: [this.replaceTargetPath] });
      }

      this.replaceTargetPath = '';
      this.emitChange();
    },
    async downloadNode(node) {
      if (!node || node.isDirectory || !this.skillName) {
        return;
      }

      try {
        const result = await this.$store.dispatch('nlp/downloadSkillFile', {
          name: this.skillName,
          path: node.path,
          version: this.version || undefined
        });
        if (!result?.base64) {
          throw new Error('Missing file content');
        }

        const mimeType = result.type || 'application/octet-stream';
        const dataUrl = result.base64.startsWith('data:') ? result.base64 : `data:${mimeType};base64,${result.base64}`;
        const link = document.createElement('a');
        link.href = dataUrl;
        link.download = this.normalizeFileName(result.name || this.nodeName(node)) || 'download';
        link.click();
      } catch {
        this.$toast?.error({
          title: 'Download failed',
          message: 'File content is not available.'
        });
      }
    },
    normalizeFileName(name) {
      if (!name || typeof name !== 'string') {
        return name;
      }
      return name.replace(/["']/g, '').trim();
    },
    emitChange() {
      this.$emit('change', {
        stagedFiles: this.stagedFiles,
        newFolders: this.newFolders,
        deletePaths: this.deletePaths
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.skill-file-tree {
  border: 1px solid var(--theme-on-background-dark);
  border-radius: 4px;
  padding: 0.5rem;
  background: var(--theme-background);
  min-height: 60px;

  .empty-message {
    font-size: 0.8rem;
    color: var(--theme-on-surface);
    opacity: 0.5;
    padding: 0.5rem;
  }

  .tree-node {
    padding: 0.25rem 0.5rem;

    &.staged {
      background: color-mix(in srgb, var(--theme-primary) 8%, transparent);
    }

    .node-row {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      font-size: 0.85rem;

      .node-icon {
        display: inline-flex;
        align-items: center;
        opacity: 0.7;

        .icon-svg {
          width: 0.9rem;
          height: 0.9rem;
        }
      }

      .node-name {
        flex: 1;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .node-size {
        font-size: 0.75rem;
        opacity: 0.6;
      }

      .node-staged-badge {
        font-size: 0.7rem;
        background: var(--theme-primary);
        color: var(--theme-on-primary);
        padding: 0.1rem 0.4rem;
        border-radius: 4px;
      }
    }
  }

  .file-tree-actions {
    display: flex;
    gap: 0.5rem;
    padding-top: 0.5rem;
    border-top: 1px solid var(--theme-on-background-dark);
    margin-top: 0.5rem;
  }

  .inline-input-row {
    display: flex;
    gap: 0.5rem;
    align-items: center;
    padding: 0.25rem 0.5rem 0.25rem 1.5rem;

    .folder-name-input {
      flex: 1;
      background: var(--theme-surface);
      border: 1px solid var(--theme-on-background-dark);
      border-radius: 4px;
      padding: 0.2rem 0.5rem;
      font-size: 0.8rem;
      color: var(--theme-on-surface);
    }
  }

  .new-folder-row {
    display: flex;
    gap: 0.5rem;
    align-items: center;
    padding-top: 0.5rem;

    .folder-name-input {
      flex: 1;
      background: var(--theme-surface);
      border: 1px solid var(--theme-on-background-dark);
      border-radius: 4px;
      padding: 0.25rem 0.5rem;
      font-size: 0.85rem;
      color: var(--theme-on-surface);
    }
  }

  .upload-dialog {
    border: 1px solid var(--theme-primary);
    border-radius: 4px;
    padding: 0.75rem;
    margin-top: 0.5rem;
    background: var(--theme-surface);

    .upload-dialog-title {
      font-weight: 600;
      font-size: 0.85rem;
      margin-bottom: 0.5rem;
    }

    .destination-options {
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
      font-size: 0.85rem;
      margin-bottom: 0.5rem;

      label {
        cursor: pointer;
        display: flex;
        align-items: center;
        gap: 0.35rem;
      }
    }

    .upload-dialog-files {
      font-size: 0.8rem;
      opacity: 0.7;
      margin-bottom: 0.5rem;
    }

    .upload-dialog-actions {
      display: flex;
      gap: 0.5rem;
    }
  }

  .action-btn {
    font-size: 0.75rem;
    padding: 0.2rem 0.5rem;
    border: 1px solid var(--theme-on-background-dark);
    border-radius: 4px;
    background: var(--theme-surface);
    color: var(--theme-on-surface);
    cursor: pointer;
    white-space: nowrap;

    &.icon-btn {
      padding: 0.2rem;
      line-height: 1;
      border-color: transparent;
      background: transparent;
      color: var(--theme-on-surface);
      display: inline-flex;
      align-items: center;

      .icon-svg {
        width: 1rem;
        height: 1rem;
      }

      &:hover {
        background: var(--theme-on-background-dark);
        border-color: var(--theme-on-background-dark);
      }
    }

    &.icon-text-btn {
      display: inline-flex;
      align-items: center;
      gap: 0.35rem;

      .icon-svg {
        width: 0.85rem;
        height: 0.85rem;
      }
    }

    &:not(.icon-btn):hover {
      background: var(--theme-on-background-dark);
    }

    &.delete-btn,
    &.remove-btn {
      color: var(--theme-error, #c0392b);
    }
  }
}
</style>
